import React, { useEffect, useState } from 'react';

import { Modal, RadioChangeEvent } from 'antd';
import styled from 'styled-components';

import Button from '../../lib/Button';
import Radio from '../../lib/Radio';
import { SAVE_PROMPT_ACTIONS } from '../constants';
import { useShopDataModalContext } from '../ShopDataModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 30px 30px 20px 30px;
`;

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-content {
      padding: 0px;
      line-height: 1;
      border-radius: 1px;
      user-select: none;
    }
    .ant-modal-body {
      padding: 0px;
      line-height: normal;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-size: 20px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const Subtitle = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
`;

const Content = styled.div`
  display: flex;
  padding-top: 20px;
  padding-bottom: 40px;
`;

const Footer = styled.div`
  display: flex;
  border-top: 1px solid var(--line-gray);
  padding: 20px 30px;
  gap: 10px;
  justify-content: end;
`;

interface Props {
  onConfirm: () => void;
  onClose: () => void;
  onCancel: () => void;
}

const ShopDataModalSavePromptModal = ({ onConfirm, onClose, onCancel }: Props) => {
  const { validateData, requestLoading } = useShopDataModalContext();
  const [action, setAction] = useState<string>(SAVE_PROMPT_ACTIONS.SAVE);
  const [saveDisabled, setSaveDisabled] = useState(false);

  useEffect(() => {
    const handleValidation = async () => {
      validateData(
        () => {
          setSaveDisabled(false);
        },
        () => {
          setSaveDisabled(true);
          setAction(SAVE_PROMPT_ACTIONS.DISCARD);
        }
      );
    };
    handleValidation();
  }, []);

  const handleConfirm = () => {
    if (action === SAVE_PROMPT_ACTIONS.SAVE) {
      onConfirm();
    } else {
      onClose();
    }
  };

  return (
    <StyledModal open={true} footer={null} destroyOnClose onCancel={onCancel}>
      <Container>
        <Header>
          <Title>Save Data?</Title>
          <Subtitle>You are about to close this screen. Please select an option below.</Subtitle>
        </Header>
        <Content>
          <Radio.Group
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
            value={action}
            onChange={(e: RadioChangeEvent) => {
              setAction(e.target.value);
            }}
          >
            <Radio className={'large'} value={SAVE_PROMPT_ACTIONS.SAVE} disabled={saveDisabled}>
              Save and close
            </Radio>
            <Radio className={'large'} value={SAVE_PROMPT_ACTIONS.DISCARD}>
              Discard all and close
            </Radio>
          </Radio.Group>
        </Content>
      </Container>
      <Footer>
        <Button
          type={'primary'}
          disabled={requestLoading}
          loading={requestLoading}
          style={{ fontFamily: 'var(--font-bold)', height: 40, width: 120 }}
          onClick={handleConfirm}
          htmlType="submit"
        >
          Confirm
        </Button>
        <Button style={{ fontFamily: 'var(--font-bold)', height: 40, width: 120 }} onClick={onCancel}>
          Back
        </Button>
      </Footer>
    </StyledModal>
  );
};

export default ShopDataModalSavePromptModal;
